import { useCallback, useSyncExternalStore } from "react";
import { HERO_SCENES, type HeroBackgroundTheme } from "~/components/hero-background/scenes";
import { readStoredPreferences, useUserPreferences } from "~/hooks/useUserPreferences";

/** Signals suit any customer; helix and cells speak to particular sciences. */
export const DEFAULT_HERO_BACKGROUND: HeroBackgroundTheme = "signals";

// `useLocalStorage` never syncs same-tab writes, so the Appearance tab
// broadcasts here and the banner re-reads.
const HERO_BACKGROUND_EVENT = "sc-hero-background-change";

const subscribe = (onStoreChange: () => void) => {
  window.addEventListener(HERO_BACKGROUND_EVENT, onStoreChange);
  return () => window.removeEventListener(HERO_BACKGROUND_EVENT, onStoreChange);
};

// A stored name that no longer matches a scene falls back to the default.
const readHeroBackground = (): HeroBackgroundTheme => {
  const stored = readStoredPreferences().heroBackground;
  return stored && stored in HERO_SCENES ? stored : DEFAULT_HERO_BACKGROUND;
};

const readHasChosen = () => readStoredPreferences().heroBackground !== undefined;

/** The animated scene behind the Home banner, whether the user has ever picked
 *  one, and the way to change it. */
export const useHeroBackground = () => {
  const [, updatePreferences] = useUserPreferences();
  const heroBackground = useSyncExternalStore(subscribe, readHeroBackground);
  const hasChosenHeroBackground = useSyncExternalStore(subscribe, readHasChosen);

  const setHeroBackground = useCallback(
    (theme: HeroBackgroundTheme) => {
      updatePreferences({ heroBackground: theme });
      window.dispatchEvent(new Event(HERO_BACKGROUND_EVENT));
    },
    [updatePreferences]
  );

  return { heroBackground, hasChosenHeroBackground, setHeroBackground };
};
